import { Box, TableCell, Typography, type TypographyOwnProps } from "@mui/material"
import type { ReactNode } from "react"
import { Minus } from "react-feather"
import { OperationStateEnum } from "../../AdherentMarketingOperations"
import {
  DangerCircle,
  SuccessCircle,
  WarnCircle,
} from "../../AdherentMarketingOperations/AdhMarketingOPList/icons"

const StatusLayout = ({
  IconElement,
  color,
  label,
}: {
  IconElement: ReactNode
  color?: TypographyOwnProps["color"]
  label: string
}) => (
  <Box display="flex" alignItems="center" gap={1}>
    {IconElement}
    <Typography
      color={color ?? "grey.light"}
      fontSize={({ typography }) => typography.pxToRem(12)}
    >
      {label}
    </Typography>
  </Box>
)

export const SuccessStatus = ({ label }: { label: string }) => (
  <StatusLayout IconElement={<SuccessCircle />} color="success.main" label={label} />
)

export const WarnStatus = ({ label }: { label: string }) => (
  <StatusLayout IconElement={<WarnCircle />} label={label} />
)

const DangerStatus = ({ icon, label }: { icon?: ReactNode; label: string }) => (
  <StatusLayout IconElement={<DangerCircle icon={icon} />} label={label} />
)

const HiddenStatus = ({ label }: { label: string }) => (
  <StatusLayout
    IconElement={
      <DangerCircle sx={{ borderColor: "grey.extraLight", color: "grey.light" }} />
    }
    label={label}
  />
)

const SubscriptionState = ({
  state,
  hidden = false,
}: { state: OperationStateEnum; hidden?: boolean }) => {
  if (hidden) return <HiddenStatus label="Ne souhaite pas participer" />

  switch (state) {
    case OperationStateEnum.REGISTRABLE:
    case OperationStateEnum.UPCOMING:
      return <WarnStatus label="Non inscrit" />
    case OperationStateEnum.WAITING:
      return <WarnStatus label="À venir" />
    case OperationStateEnum.REVIEWED:
      return <WarnStatus label="Validation en cours" />
    case OperationStateEnum.REJECTED:
      return <DangerStatus label="Justificatif refusé" />
    case OperationStateEnum.VALIDATED:
    case OperationStateEnum.STARTED:
      return <SuccessStatus label="Inscrit" />
    case OperationStateEnum.EXPIRED:
      return <DangerStatus icon={<Minus />} label="Non participé" />
    case OperationStateEnum.OUTDATED:
      return <DangerStatus icon={<Minus />} label="Inscription clôturée" />
    default:
      return null
  }
}

interface ParticipationCellProps {
  registered: boolean
  state: OperationStateEnum
  hidden?: boolean
}

export const AdhParticipationCell = ({
  registered,
  state,
  hidden,
}: ParticipationCellProps) => {
  return (
    <TableCell>
      {registered ? (
        <SuccessStatus label="Inscrit" />
      ) : (
        <SubscriptionState state={state} hidden={hidden} />
      )}
    </TableCell>
  )
}
